import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Link,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { fetchUpdatePassword } from 'src/redux/employerUser'

const Security = ({ employerUser }) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    currentPassword: '',
    password: '',
    confirmPassword: '',
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  })
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
    setSuccess(false)
  }

  const toggleShowPassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required'
    }

    if (!formData.password) {
      newErrors.password = 'New password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      await dispatch(
        fetchUpdatePassword({
          id: employerUser.id,
          currentPassword: formData.currentPassword,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        })
      )

      // Reset form on success
      setFormData({
        currentPassword: '',
        password: '',
        confirmPassword: '',
      })
      setSuccess(true)
    } catch (error) {
      // Extract specific error messages from API response
      const apiErrors = error.response?.data?.errors || {}
      const newErrors = {}

      // Map API error fields to form fields
      if (apiErrors.currentPassword) {
        newErrors.currentPassword = Array.isArray(apiErrors.currentPassword)
          ? apiErrors.currentPassword[0]
          : apiErrors.currentPassword
      } else if (apiErrors.current_password) {
        newErrors.currentPassword = Array.isArray(apiErrors.current_password)
          ? apiErrors.current_password[0]
          : apiErrors.current_password
      }

      if (apiErrors.password) {
        newErrors.password = Array.isArray(apiErrors.password)
          ? apiErrors.password[0]
          : apiErrors.password
      }

      if (apiErrors.confirmPassword) {
        newErrors.confirmPassword = Array.isArray(apiErrors.confirmPassword)
          ? apiErrors.confirmPassword[0]
          : apiErrors.confirmPassword
      } else if (apiErrors.confirm_password) {
        newErrors.confirmPassword = Array.isArray(apiErrors.confirm_password)
          ? apiErrors.confirm_password[0]
          : apiErrors.confirm_password
      }

      // If no specific field errors, show a general error
      if (Object.keys(newErrors).length === 0) {
        const errorMessage = error.response?.data?.message ||
                           error.response?.data?.error ||
                           'Failed to update password. Please try again.'
        newErrors.currentPassword = errorMessage
      }

      setErrors(newErrors)
    }
  }

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      password: '',
      confirmPassword: '',
    })
    setErrors({})
    setSuccess(false)
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Change Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your password to keep your account secure
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Password updated successfully!
        </Alert>
      )}

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Current Password"
          type={showPasswords.current ? 'text' : 'password'}
          value={formData.currentPassword}
          onChange={(e) => handleChange('currentPassword', e.target.value)}
          error={!!errors.currentPassword}
          helperText={errors.currentPassword}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleShowPassword('current')}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="New Password"
          type={showPasswords.new ? 'text' : 'password'}
          value={formData.password}
          onChange={(e) => handleChange('password', e.target.value)}
          error={!!errors.password}
          helperText={errors.password || 'Must be at least 8 characters'}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleShowPassword('new')}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirm New Password"
          type={showPasswords.confirm ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={(e) => handleChange('confirmPassword', e.target.value)}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleShowPassword('confirm')}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Update Password
        </Button>
      </Stack>

      {/* Profile Deletion Notice */}
      <Divider sx={{ mt: 4, mb: 3 }} />
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          If you want to delete your profile, please contact us at{' '}
          <Link href="mailto:contact@mystethi.com" underline="hover">
            contact@mystethi.com
          </Link>
        </Typography>
      </Box>
    </Box>
  )
}

export default Security
